import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { type LegalLink } from '@/services/legalService';
import { useContent } from '@/hooks/useContent';
import { LegalDocumentModal } from '@/components/common/LegalDocumentModal';

interface Props {
  links: (LegalLink | null)[];
  /** 'caption' for the pre-login footer; 'body2' for the app-shell footer */
  variant?: 'caption' | 'body2';
  spacing?: number;
  /**
   * 'inherit' — muted page chrome (hover → primary).
   * 'primary' — brand purple links.
   * 'textSecondary' — Portal Redesign 2026 in-card footer (grey text, stays grey).
   */
  color?: 'inherit' | 'primary' | 'textSecondary';
}


const FONT_SIZE: Record<'caption' | 'body2', string> = {
  caption: '0.6875rem',
  body2:   '0.75rem',
};

/**
 * Renders a row of legal document links.
 *
 * Visibility rules:
 *   - null entries (no document configured for the facility) → hidden
 *   - non-null entries (document row exists and is active) → always shown
 *
 * Link behaviour:
 *   - url present  → opens in a new tab (external)
 *   - no url + content present → opens inline modal viewer
 *   - no url + no content      → non-interactive label text
 */
export function LegalFooterLinks({
  links,
  variant = 'caption',
  spacing = 2,
  color = 'inherit',
}: Readonly<Props>) {
  
  const { getContent } = useContent();
  const [modalLink, setModalLink] = useState<LegalLink | null>(null);

  const configured = links.filter((l): l is LegalLink => l !== null);
  if (configured.length === 0) return null;

  const resolveLabel = (link: LegalLink): string =>
    getContent(link.title) || link.title;

  const isExternal = (url: string) => /^https?:\/\//i.test(url);
  const fontSize = FONT_SIZE[variant];
  const cardFooterLinkSx = {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '22px',
    minWidth: 0,
    textTransform: 'capitalize' as const,
    px: '5px',
    py: 0.5,
  };
  const buttonSx =
    color === 'primary'
      ? cardFooterLinkSx
      : color === 'textSecondary'
        ? {
            ...cardFooterLinkSx,
            color: 'text.secondary',
            '&:hover, &:visited': { color: 'text.secondary', bgcolor: 'transparent' },
          }
        : { fontSize, minWidth: 0, '&:hover': { color: 'primary.main' } };
  const buttonColor = color === 'textSecondary' ? 'inherit' : color;

  return (
    <>
      <Stack direction="row" spacing={spacing} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {configured.map((link) => {
          const label = resolveLabel(link);

          if (link.url) {
            return (
              <Button
                key={link.documentType}
                component="a"
                href={link.url}
                {...(isExternal(link.url) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                size="small"
                color={buttonColor}
                sx={buttonSx}
              >
                {label}
              </Button>
            );
          }

          if (link.content) {
            return (
              <Button
                key={link.documentType}
                size="small"
                color={buttonColor}
                onClick={() => setModalLink(link)}
                sx={buttonSx}
              >
                {label}
              </Button>
            );
          }

          return (
            <Typography key={link.documentType} variant={variant} color="text.disabled" component="span" sx={{ fontSize }}>
              {label}
            </Typography>
          );
        })}
      </Stack>

      <LegalDocumentModal link={modalLink} onClose={() => setModalLink(null)} />
    </>
  );
}
